import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  IdentificationIcon,
  CalendarIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Badge, getStatusBadgeVariant, InsurerLogo, Modal, ModalFooter, ContentLoader, BackButton } from '@/components/common';
import { PolicyNumber } from '@/components/common/PolicyNumber';
// Imported by path rather than through components/common/index.ts, which is
// outside this task's file domain.
import { RecordTimestamps } from '@/components/common/RecordTimestamps';
import { RevealableIdentity } from '@/components/common/RevealableIdentity';
import { isMaskedIdentityField, revealCustomerIdentity } from '@/services/identityReveal';
import { ContactAvatar, type ContactAvatarImage } from '@/components/common/ContactAvatar';
import { useCustomer, useDeleteCustomer, useCreateCustomerLogin } from '@/hooks';
import { useCan } from '@/hooks/useEmployeeRights';
import api, { getErrorMessage } from '@/services/api';
import { formatDate, formatCurrency, kycStatusLabel, titleCase } from '@/utils';
import { getIdentityFields, IDENTITY_FIELD_LABELS } from '@/utils/customerBasicInfo';
import type { Customer, CustomerCreateLoginResponse, PaginatedResponse, Policy } from '@/types';
import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/store';
import { ContactRecordTabs } from './ContactRecordTabs';
import { ContactQuickActions } from './ContactQuickActions';
import { KycDocumentsPanel } from './KycDocumentsPanel';
// B15: by path, not `@/services` — that index is outside this task's file domain.
import { resolveNewQuoteHref } from '@/services/leads';
// Item 7a. Lives under pages/quotes because it is a fact about QUOTES -- see
// its own header for why `is_shared` is the only thing the tag may be derived
// from. Imported by path: hooks/ and components/common/index.ts are outside
// this task's file domain, the same reason RecordTimestamps is imported by path
// above.
import { ContactQuotesPendingTag } from '@/pages/quotes/quotesPending';

// ── Login provisioning (item 8: cross-broker customer login) ────────
// Broker-admin-only control that mirrors PospOnboardingPanel's
// PospLoginSection / PospLoginCredentialsModal pattern
// (frontend/src/pages/brokers/PospOnboardingPanel.tsx): a temp password is
// shown exactly once, or -- if this customer's phone already matches an
// existing login -- the two are linked and no new password is generated.

function CustomerLoginResultModal({
  result,
  onClose,
}: {
  result: CustomerCreateLoginResponse | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Modal isOpen={!!result} onClose={onClose} title="Customer Login" size="md">
      {result && (
        <div className="space-y-4">
          {!result.credentials ? (
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 dark:bg-blue-900/20 dark:border-blue-800">
              <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Linked to an existing login</p>
              <p className="mt-1 text-xs text-blue-700 dark:text-blue-400">{result.message}</p>
              {/* B3: this branch used to be the end of the road -- linked, no
                  password, nothing to do next. */}
              {(result as LoginResult).reissue_available && (
                <p className="mt-2 text-xs text-blue-700 dark:text-blue-400">
                  If they have lost their password, close this and use{' '}
                  <span className="font-medium">Issue a new password</span>.
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 dark:bg-amber-900/20 dark:border-amber-800">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  {(result as LoginResult).reissued
                    ? 'This replaces their previous password, and is shown only once.'
                    : 'This password is shown only once.'}
                </p>
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{result.message}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Login (email / phone)
                </label>
                <input
                  type="text"
                  readOnly
                  value={result.credentials.email || result.credentials.phone || '—'}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Temporary Password
                </label>
                <div className="mt-1 flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={result.credentials.password}
                    className="block w-full rounded-md border-gray-300 bg-gray-50 font-mono shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                  />
                  <Button type="button" variant="outline" onClick={() => handleCopy(result.credentials!.password)}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  The customer must change this password on first login.
                </p>
              </div>
            </>
          )}
        </div>
      )}
      <ModalFooter>
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * F2: `avatar`/`avatar_image` are on `CustomerDetailSerializer` but not on the
 * shared `Customer` type in `types/index.ts`, which is outside this task's file
 * domain — so the extra read shape is declared locally, as `InsurersListPage`
 * does for its own image fields.
 */
type CustomerWithAvatar = Customer & {
  avatar?: string | null;
  avatar_image?: ContactAvatarImage | null;
};

/**
 * B3: the login state the contact page can now see, and the recovery path.
 *
 * Generation already worked. What did not exist was any way BACK: re-calling
 * create-login on a linked contact answers `credentials: null`, the admin reset
 * under `/users/{id}/reset_password/` cannot reach a customer-role account at
 * all, and `/forgot-password` is a dead link on every login page. A customer
 * who lost the temporary password had nothing.
 *
 * `login_status` is read-only detail from `CustomerDetailSerializer`;
 * `must_change_password` had been written by every provisioning since item 8
 * and read by nothing at all.
 */
type CustomerLoginStatus = {
  has_login: boolean;
  must_change_password: boolean;
  last_login: string | null;
  is_active: boolean;
  can_reissue: boolean;
};

type CustomerWithLogin = Customer & { login_status?: CustomerLoginStatus | null };

/**
 * B2: `city` is an FK into the seeded `geography.City` table, so
 * `CustomerDetailSerializer` answers it as a bare id and this page printed a raw
 * UUID in the middle of the address. `city_name` is the readable half; it has
 * been on the detail payload since B2 and nothing read it. The shared `Customer`
 * type lives in `types/index.ts`, outside this task's file domain, so the read
 * shape is declared locally like the two above.
 */
type CustomerWithCityName = Customer & { city_name?: string | null };

/** The one-line address the contact page shows. `city_name`, never `city` --
 *  the latter is a UUID and belongs in no sentence a human reads. */
export function contactAddressLine(customer: CustomerWithCityName): string {
  if (!customer.address_line1) return '-';
  const head = [customer.address_line1, customer.address_line2, customer.city_name, customer.state]
    .filter(Boolean)
    .join(', ');
  return customer.pincode ? `${head} - ${customer.pincode}` : head;
}

/** `reissue_available` rides on the create-login answer; the shared `Customer`
 *  types live in `types/index.ts`, outside this task's file domain. */
type LoginResult = CustomerCreateLoginResponse & { reissue_available?: boolean; reissued?: boolean };

function CustomerLoginSection({ customer }: { customer: Customer }) {
  const createLoginMutation = useCreateCustomerLogin();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<LoginResult | null>(null);
  const [reissueError, setReissueError] = useState<string | null>(null);

  const login = (customer as CustomerWithLogin).login_status ?? null;

  const handleProvision = () => {
    setReissueError(null);
    createLoginMutation.mutate(customer.id, {
      onSuccess: (data) => setResult(data as LoginResult),
    });
  };

  const reissueMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<LoginResult>(`/customers/${customer.id}/reset-login/`);
      return data;
    },
    onSuccess: (data) => {
      setReissueError(null);
      setResult(data);
      // `login_status.must_change_password` has just changed.
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
    },
    // NOT swallowed: the refusal (a login shared beyond this broker) is the
    // whole reason the button might not work, and it has to be readable.
    onError: (error) => setReissueError(getErrorMessage(error)),
  });

  return (
    <Card>
      <CardHeader
        title="Customer Login"
        subtitle={
          customer.user
            ? 'This customer already has a login linked.'
            : 'Issue login credentials so this customer can view their policies online.'
        }
      />

      {login?.has_login && (
        <div className="mb-3 space-y-1 text-xs text-gray-600 dark:text-gray-300">
          <p>
            {login.must_change_password
              ? 'The temporary password has not been changed yet — they have not signed in with it.'
              : 'They have set their own password.'}
          </p>
          <p>
            {login.last_login
              ? `Last signed in ${formatDate(login.last_login)}.`
              : 'They have never signed in.'}
          </p>
          {!login.is_active && (
            <p className="font-medium text-amber-700 dark:text-amber-300">This login is disabled — they cannot sign in.</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={handleProvision} disabled={createLoginMutation.isPending}>
          {createLoginMutation.isPending
            ? 'Provisioning...'
            : customer.user
              ? 'Re-link / Refresh Login'
              : 'Provision Login'}
        </Button>

        {login?.has_login && login.can_reissue && (
          <Button
            variant="outline"
            onClick={() => reissueMutation.mutate()}
            disabled={reissueMutation.isPending}
          >
            {reissueMutation.isPending ? 'Issuing…' : 'Issue a new password'}
          </Button>
        )}
      </div>

      {login?.has_login && !login.can_reissue && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          A new password cannot be issued from here: this login is used beyond this contact record,
          so changing it would change their password everywhere at once.
        </p>
      )}

      {reissueError && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400" data-testid="reissue-error">
          {reissueError}
        </p>
      )}

      <CustomerLoginResultModal result={result} onClose={() => setResult(null)} />
    </Card>
  );
}

// ── Policies: Active / Archive + upcoming renewals (tasks 9 and 13(3)) ──────
// The split is driven by `?display_status=active|inactive`, which the policy
// list has had all along and which mirrors `Policy.is_active` exactly —
// date-based, and a policy cancelled before its expiry date is forced Inactive
// rather than reading Active on dates alone. Re-deriving "active" here from
// `status` or `expiry_date` would put a second, drifting rule on the screen.
const POLICY_TABS = [
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Archive' },
] as const;

type PolicyTabId = (typeof POLICY_TABS)[number]['id'];

/** How many renewals the panel lists; the rest are one click away under Policies. */
const RENEWALS_SHOWN = 5;

/** `GET /policies/expiring_soon/` — the paginated envelope plus the two counts
 *  the badges show, computed server-side over the same filtered book. */
type ExpiringSoonResponse = PaginatedResponse<Policy> & {
  within_15: number;
  within_30: number;
};

/** `days_to_expiry` rides on every `PolicyListSerializer` row already
 *  (backend/apps/policies/serializers.py) — computed against the SERVER's
 *  date. It is not on the shared `Policy` type in `types/index.ts`, which is
 *  outside this task's file domain, so the extra read shape is declared here,
 *  the same way this file declares `avatar_image` above.
 *
 *  Nothing in this file re-derives the number from the browser clock. A client
 *  an hour either side of the server's midnight would otherwise print a
 *  different figure from the one the policy list, the row badge and the
 *  renewal worklist all print — and "expires today" versus "overdue by 1 day"
 *  is not a rounding difference to a broker chasing a renewal. */
type PolicyWithDays = Policy & { days_to_expiry?: number | null };

function servedDaysToExpiry(policy: Policy): number | null {
  const days = (policy as PolicyWithDays).days_to_expiry;
  return typeof days === 'number' ? days : null;
}

/** Rows per page of the contact's policy table. Matches the server's
 *  StandardResultsSetPagination default, so the "Showing X–Y of N" line and the
 *  slice actually returned cannot drift. */
const POLICY_PAGE_SIZE = 20;

/** What `GET /policies/` really returns: DRF's StandardResultsSetPagination adds
 *  `total_pages`/`current_page`/`page_size` on top of `PaginatedResponse`. */
type PolicyPage = PaginatedResponse<Policy> & {
  total_pages?: number;
  current_page?: number;
  page_size?: number;
};

/**
 * One half (Active or Archive) of THIS CONTACT's book, a page at a time.
 *
 * `?customer_group=` — not `?customer=`. A contact is not always one
 * `Customer` row: pre-existing duplicate rows (same broker, same name, matching
 * phone or email) each hold their own policies, and `?customer=` is a
 * single-FK filter that cannot express "this person". On the live book that
 * difference is 11 policy rows across 5 contacts — and for `caa82c1b-…`
 * ("Lalit .") it is the contact's ENTIRE book of 6, because that row owns none
 * of its own policies. `?customer_group=` unions the duplicates server-side, so
 * `display_status` partitions and counts the WHOLE group and the header, the
 * tabs, the table and the renewals card all describe the same set of policies.
 *
 * It reads `api` directly rather than going through `usePolicies`, because
 * `policyService.getPolicies` copies a fixed list of filter keys into the query
 * string and would silently drop `customer_group`; `src/services/policies.ts` is
 * outside this task's file domain. `CustomerRenewalsCard` below already reads
 * its own endpoint this way.
 */
function useContactPolicies(customerId: string, displayStatus: PolicyTabId, page: number) {
  return useQuery({
    queryKey: ['contact-policies', customerId, displayStatus, page],
    queryFn: async () =>
      (
        await api.get<PolicyPage>(
          `/policies/?customer_group=${customerId}&display_status=${displayStatus}` +
            // Board #279: `archived=all`. A contact's history is a record of
            // everything they ever bought, so archiving a policy out of the
            // Policies LIST must not also delete it from the person's file --
            // which is what the default (live records only) would do here.
            `&archived=all&page=${page}&page_size=${POLICY_PAGE_SIZE}`
        )
      ).data,
    enabled: !!customerId && customerId !== 'undefined',
    // Paging keeps the current page on screen while the next one loads, rather
    // than flashing the "no policies" empty state between pages.
    placeholderData: (previous) => previous,
  });
}

function dueLabel(days: number): string {
  if (days < 0) return `overdue by ${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'}`;
  if (days === 0) return 'expires today';
  return `in ${days} ${days === 1 ? 'day' : 'days'}`;
}

function dueVariant(days: number) {
  if (days <= 0) return 'error' as const;
  if (days <= 15) return 'warning' as const;
  if (days <= 30) return 'info' as const;
  return 'default' as const;
}

function CustomerRenewalsCard({
  customerId,
  policyCount,
}: {
  customerId: string;
  policyCount: number;
}) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    // `days=all` deliberately drops the upper bound. A real book expires far
    // out — the live contact "Om Logistics Limited" has 173 policies and the
    // earliest expires 2027-04-14 — so a fixed 15/30-day window renders an
    // empty panel for a contact whose every policy is live, which reads as
    // "nothing is due" rather than "nothing is due YET". The endpoint answers
    // soonest-first and carries `within_15`/`within_30`, so the badges below
    // and the rows under them are computed over one and the same queryset.
    queryKey: ['customer-renewals-group', customerId],
    queryFn: async () =>
      (
        await api.get<ExpiringSoonResponse>(
          `/policies/expiring_soon/?customer_group=${customerId}&days=all&page_size=${RENEWALS_SHOWN}`
        )
      ).data,
    enabled: !!customerId && customerId !== 'undefined',
  });

  const renewals = data?.results ?? [];

  return (
    <Card>
      <CardHeader
        title="Upcoming Renewals"
        subtitle={
          data && data.count > renewals.length
            ? `Soonest ${renewals.length} of ${data.count} live policies`
            : 'Live policies, soonest expiry first'
        }
      />
      <div className="flex flex-wrap gap-2 mb-4">
        <Badge variant="warning">{data?.within_15 ?? 0} in 15 days</Badge>
        <Badge variant="info">{data?.within_30 ?? 0} in 30 days</Badge>
      </div>
      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading renewals…</p>
      ) : renewals.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {policyCount === 0
            ? 'No policies yet, so nothing to renew.'
            : `None of this contact's ${policyCount} ${policyCount === 1 ? 'policy is' : 'policies are'} live right now — nothing is up for renewal.`}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700" aria-label="Upcoming renewals">
          {renewals.map((p) => {
            const days = servedDaysToExpiry(p);
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/policies/${p.id}`)}
                  className="w-full flex flex-wrap items-center justify-between gap-2 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-primary-600 dark:text-primary-400 truncate">
                      <PolicyNumber source={p} />
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                      {p.vehicle_number || p.product_name || '—'}
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {p.expiry_date ? formatDate(p.expiry_date) : '-'}
                    </span>
                    {days !== null && (
                      <Badge variant={dueVariant(days)} size="sm">
                        {dueLabel(days)}
                      </Badge>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const hasAddPolicyRight = useCan('policies', 'create');
  const hasAddQuoteRight = useCan('quotes', 'create');
  // Item 4 / B4: verifying or rejecting a KYC document is a contacts Edit
  // (`CustomerDocumentViewSet` is a child of the contact record).
  const hasKycVerifyRight = useCan('contacts', 'edit');
  const canEditContact = useCan('contacts', 'edit');
  const canDeleteContact = useCan('contacts', 'delete');
  // Broker Add Policy is not available to country_admin (item 4); a gated role
  // additionally needs policies/quotes Create.
  const canAddPolicy = user?.role !== 'country_admin' && hasAddPolicyRight;
  const canAddQuote = hasAddQuoteRight;
  // Item 8 / B4: creating or resetting a customer's login hands out
  // credentials, so `create_login` and `reset_login` are ADMIN_ONLY on the
  // server -- broker employees lost them in B4 and are not shown the section.
  const canManageLogin =
    user?.role === 'super_admin' ||
    user?.role === 'country_admin' ||
    user?.role === 'broker_admin';
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const recordsRef = useRef<HTMLDivElement>(null);
  // `?tab=records` -- how the Contacts list's "View Policies" asks this page to
  // open at the records. A bare #fragment did nothing: nothing scrolls to it,
  // so the reader arrived at the top with the records below the fold. Runs
  // after the section has mounted, hence the effect rather than a render-time
  // scroll.
  const [searchParams] = useSearchParams();
  const wantsRecords = searchParams.get('tab') === 'records';
  useEffect(() => {
    // Keyed off the ref being populated rather than off `customer` (which is
    // declared below this point): the section can only be scrolled to once it
    // has actually mounted, which is the same condition.
    if (wantsRecords && recordsRef.current) {
      recordsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [wantsRecords, recordsRef.current]);
  const [policyTab, setPolicyTab] = useState<PolicyTabId>('active');
  const [policyPage, setPolicyPage] = useState(1);

  const { data: customer, isLoading, error } = useCustomer(id!);
  // Active / Archive are fetched separately so each tab's count is the server's
  // count for that half of the contact's WHOLE group (see useContactPolicies),
  // not a slice of one page of rows. Only the tab on screen is paged; the other
  // is held at page 1, which is all its badge needs.
  const activePolicies = useContactPolicies(
    id!,
    'active',
    policyTab === 'active' ? policyPage : 1
  );
  const archivedPolicies = useContactPolicies(
    id!,
    'inactive',
    policyTab === 'inactive' ? policyPage : 1
  );
  const tabQuery = policyTab === 'active' ? activePolicies : archivedPolicies;
  const policies = tabQuery.data?.results ?? [];
  const policiesLoading = tabQuery.isLoading;
  const tabCounts: Record<PolicyTabId, number> = {
    active: activePolicies.data?.count ?? 0,
    inactive: archivedPolicies.data?.count ?? 0,
  };
  // The header prints the server's count for the tab; the table can only render
  // one page of it. Everything below is what keeps those two honest with each
  // other -- 173 policies under a 20-row table with no pager is a screen
  // contradicting itself.
  const shownCount = tabCounts[policyTab];
  const pageCount =
    tabQuery.data?.total_pages ?? Math.max(1, Math.ceil(shownCount / POLICY_PAGE_SIZE));
  const firstRowShown = shownCount === 0 ? 0 : (policyPage - 1) * POLICY_PAGE_SIZE + 1;
  const lastRowShown = Math.min(policyPage * POLICY_PAGE_SIZE, shownCount);
  const deleteMutation = useDeleteCustomer();

  // B4: what the server said when it refused. Kept on screen rather than left
  // to the toast, because the refusal is a list of things the reader has to go
  // and deal with -- "3 policies and 1 claim" is not a message you skim once
  // and it disappears.
  const [deleteRefusal, setDeleteRefusal] = useState<{
    message: string;
    blockedBy: Record<string, number>;
  } | null>(null);

  const handleDelete = () => {
    setDeleteRefusal(null);
    deleteMutation.mutate(id!, {
      onSuccess: () => {
        navigate('/contacts');
      },
      onError: (error) => {
        const data = (error as { response?: { data?: unknown } })?.response?.data as
          | { detail?: string; blocked_by?: Record<string, number> }
          | undefined;
        setDeleteRefusal({
          message: data?.detail || getErrorMessage(error),
          blockedBy: data?.blocked_by ?? {},
        });
      },
    });
  };

  if (isLoading) {
    return <ContentLoader />;
  }

  if (error || !customer) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Contact not found</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">The contact you are looking for does not exist.</p>
        <Link to="/contacts" className="mt-4 inline-block">
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
            Back to Contacts
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/contacts" />
      </div>
      {/* F2: the profile picture sits beside the record header rather than
          inside it — PageHeader takes a plain string title and is outside this
          task's file domain. `avatar_image` is already on
          CustomerDetailSerializer; nothing rendered it before. */}
      <div className="flex items-center gap-4">
        <ContactAvatar
          contactId={customer.id}
          name={customer.full_name}
          image={(customer as CustomerWithAvatar).avatar_image}
          size="lg"
        />
        <div className="flex-1 min-w-0">
          <PageHeader
            title={customer.full_name}
            subtitle={`Customer Code: ${customer.customer_code}`}
            breadcrumbs={[
              { label: 'Contacts', href: '/contacts' },
              { label: customer.full_name },
            ]}
            actions={
              <div className="flex flex-wrap items-center gap-3">
                {/* Item 6: the four things you do to a contact, top-right.
                    "View Policies" scrolls to the records tabs rather than
                    navigating -- the policies are already on this page, and
                    sending the reader to a filtered list would take them
                    further from the record they are reading. */}
                <ContactQuickActions
                  contactId={id!}
                  contactName={customer.full_name}
                  onViewPolicies={() =>
                    recordsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                />
                {canEditContact && (
                  <Link to={`/contacts/${id}/edit`}>
                    <Button variant="outline" leftIcon={<PencilIcon className="w-5 h-5" />}>
                      Edit
                    </Button>
                  </Link>
                )}
                {canDeleteContact && (
                  <Button
                    variant="danger"
                    leftIcon={<TrashIcon className="w-5 h-5" />}
                    onClick={() => setShowDeleteModal(true)}
                  >
                    Delete
                  </Button>
                )}
              </div>
            }
          />
          {/* Item 7a: this contact has a quote the broker has prepared but not
              yet put in front of them (`is_shared=false`). It sits under the
              name, beside "Create New Quote" -- the point of the tag is that
              you can see the outstanding quote and act on it from the same
              header. It renders nothing when there is nothing pending, and says
              so plainly when the count could not be fetched (never silence,
              which would read as "none"). */}
          <ContactQuotesPendingTag customerId={id} className="mb-4" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader title="Basic Information" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <InfoItem label="Full Name" value={customer.full_name} />
              <InfoItem label="Customer Type" value={titleCase(customer.customer_type)} />
              <InfoItem label="Segment" value={titleCase(customer.segment)} />
              <InfoItem label="Gender" value={customer.gender ? titleCase(customer.gender) : '-'} />
              <InfoItem
                label="Date of Birth"
                value={customer.date_of_birth ? formatDate(customer.date_of_birth) : '-'}
              />
              <InfoItem label="Marital Status" value={customer.marital_status ? titleCase(customer.marital_status) : '-'} />
              <InfoItem label="Occupation" value={customer.occupation || '-'} />
              <InfoItem label="Annual Income" value={customer.annual_income || '-'} />
              <InfoItem label="Nationality" value={customer.nationality || '-'} />
            </div>
          </Card>

          {/* Contact Info */}
          <Card>
            <CardHeader title="Contact Information" />
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <PhoneIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Phone</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{customer.phone || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <PhoneIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Alternate Phone</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{customer.alternate_phone || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <EnvelopeIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{customer.email || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <MapPinIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Address</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {contactAddressLine(customer as CustomerWithCityName)}
                  </p>
                </div>
              </div>
              {(customer as Customer & { whatsapp_number?: string }).whatsapp_number && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <PhoneIcon className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">WhatsApp</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{(customer as Customer & { whatsapp_number?: string }).whatsapp_number}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* KYC Info — identity fields are per customer_type (BROKER-3),
              mirroring the form's schema; only fields with a saved value are
              shown so blanks for the current type don't clutter the card. */}
          <Card>
            <CardHeader title="KYC Information" />
            {(() => {
              const identityFields = getIdentityFields(customer.customer_type).filter(
                (key) => !!customer[key]
              );
              if (identityFields.length === 0) {
                return <p className="text-sm text-gray-500 dark:text-gray-400">No identity or tax details on file.</p>;
              }
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {identityFields.map((key) =>
                    // #377: these four arrive masked; Reveal fetches (and audits) the full value.
                    isMaskedIdentityField(key) ? (
                      <RevealableIdentity
                        key={key}
                        label={IDENTITY_FIELD_LABELS[key]}
                        maskedValue={customer[key] || '-'}
                        canReveal={Boolean(customer.can_reveal_identity)}
                        onReveal={() => revealCustomerIdentity(customer.id, key)}
                      />
                    ) : (
                      <InfoItem
                        key={key}
                        label={IDENTITY_FIELD_LABELS[key]}
                        value={customer[key] || '-'}
                      />
                    )
                  )}
                </div>
              );
            })()}
          </Card>

          {/* KYC documents. Sits directly under the identity fields it fills:
              the values in the card above are what a confirmed reading writes,
              so the evidence and the fields belong on the same screen. Every
              role that can open this page is broker-side (App.tsx admits
              broker_admin / broker_employee / posp / super_posp), and the
              requirement is that the broker AND its POSP verify from their
              side — so the verdict controls are on for all of them. The server
              re-checks it either way.

              Item 4 narrowed that by one step, and B4 made it a module right:
              verify/reject is a contacts Edit, so an employee or POSP without
              it gets a 403. `useCan` is all-true for broker_admin/country_admin/
              super_admin and reads the caller's matrix for the other three, so
              this draws the verdict buttons only for someone who can use them
              — the same reasoning as `canEditPhoto` on the form page. */}
          <KycDocumentsPanel customerId={id!} canVerify={hasKycVerifyRight} />

          {/* Renewals — what this contact owes next, above the full book. */}
          <CustomerRenewalsCard
            customerId={id!}
            policyCount={tabCounts.active + tabCounts.inactive}
          />

          {/* Item 9: one contact's insurance in four tabs. The Policies panel
              below is PASSED IN rather than rebuilt inside the tabs -- it
              already carries the Active/Archive split, its own paging and the
              board #279 rule that an archived policy stays in a person's
              history. Two policies tables would be two answers to one
              question. */}
          <div id="contact-records" ref={recordsRef}>
            <ContactRecordTabs
              customerId={id!}
              policyCount={tabCounts.active + tabCounts.inactive}
              policiesPanel={
                <>
            <Card>
              <CardHeader
                title="Policies"
                subtitle={`${tabCounts.active} active · ${tabCounts.inactive} archived`}
              />
              <div className="border-b border-gray-200 dark:border-gray-700 mb-4 overflow-x-auto">
                <nav className="flex space-x-8">
                  {POLICY_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => {
                        setPolicyTab(tab.id);
                        // The other half is a different length; page 2 of it may
                        // not exist, and would render an empty table under a
                        // non-zero count.
                        setPolicyPage(1);
                      }}
                      aria-current={policyTab === tab.id ? 'page' : undefined}
                      className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                        policyTab === tab.id
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:hover:border-gray-600 dark:text-gray-400 dark:hover:text-gray-200'
                      }`}
                    >
                      {tab.label}
                      <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {tabCounts[tab.id]}
                      </span>
                    </button>
                  ))}
                </nav>
              </div>
              {policiesLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading policies…</p>
              ) : policies.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {policyTab === 'active'
                    ? 'No active policies for this contact.'
                    : 'Nothing archived for this contact.'}
                </p>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="min-w-full text-sm" aria-label="Policies">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                        <th className="px-4 py-2 font-medium">Policy</th>
                        {/* Task 9: `vehicle_number` was already on every row of
                            the wire (591 of 716 live policies carry one) and
                            nothing rendered it. It sits next to the policy number
                            because on a motor book the registration IS how a
                            broker recognises the row. */}
                        <th className="px-4 py-2 font-medium">Vehicle</th>
                        {/* U-03: which company wrote each policy. The contact's
                            own file never named the insurer at all -- the row
                            carried `insurer_name` all along and nothing drew
                            it -- so the logo arrives with the name rather than
                            instead of it. */}
                        <th className="px-4 py-2 font-medium">Insurer</th>
                        <th className="px-4 py-2 font-medium">Product</th>
                        <th className="px-4 py-2 font-medium">Premium</th>
                        <th className="px-4 py-2 font-medium">Status</th>
                        <th className="px-4 py-2 font-medium">Expiry</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {policies.map((p) => (
                        <tr
                          key={p.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                          onClick={() => navigate(`/policies/${p.id}`)}
                        >
                          <td className="px-4 py-2 font-medium text-primary-600 dark:text-primary-400">
                            <PolicyNumber source={p} />
                          </td>
                          <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{p.vehicle_number || '-'}</td>
                          <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                            <span className="flex items-center gap-2">
                              <InsurerLogo
                                src={p.insurer_logo_url}
                                name={p.insurer_name}
                                size="xs"
                              />
                              <span className="truncate">{p.insurer_name || '-'}</span>
                            </span>
                          </td>
                          <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{p.product_name || '-'}</td>
                          <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                            {p.total_premium ? formatCurrency(p.total_premium) : '-'}
                          </td>
                          <td className="px-4 py-2">
                            <Badge variant={getStatusBadgeVariant(p.status)}>{titleCase(p.status)}</Badge>
                          </td>
                          <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                            {p.expiry_date ? formatDate(p.expiry_date) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* The duplicate-held policies are IN the table now (see
                  useContactPolicies), so the footnote that used to apologise for
                  their absence is gone. What is left to reconcile is the page
                  against the count above it. */}
              {pageCount > 1 && (
                <nav
                  aria-label="Policy pages"
                  className="mt-3 flex flex-wrap items-center justify-between gap-3"
                >
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {`Showing ${firstRowShown}–${lastRowShown} of ${shownCount}`}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={policyPage <= 1}
                      onClick={() => setPolicyPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {`Page ${policyPage} of ${pageCount}`}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={policyPage >= pageCount}
                      onClick={() => setPolicyPage((p) => Math.min(pageCount, p + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </nav>
              )}
            </Card>
                </>
              }
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {canManageLogin && <CustomerLoginSection customer={customer} />}

          {/* Status Card */}
          <Card>
            <CardHeader title="Status" />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Account Status</span>
                <Badge variant={customer.is_active ? 'success' : 'default'}>
                  {customer.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">KYC Status</span>
                <Badge variant={getStatusBadgeVariant(customer.kyc_status)}>
                  {kycStatusLabel(customer.kyc_status)}
                </Badge>
              </div>
            </div>
          </Card>

          {/* Timeline Card */}
          <Card>
            <CardHeader title="Timeline" />
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CalendarIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Customer Since</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDate(customer.customer_since)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <IdentificationIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Source</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {sourceLabel(customer.source)}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader title="Quick Actions" />
            <div className="space-y-3">
              {canAddQuote && (
                // B15: same "most recent converted lead" prefill as the
                // header's ContactQuickActions button — resolved async so a
                // broken lookup falls back to the plain `?customer=` link
                // rather than blocking "new quote".
                //
                // A real link to the plain `?customer=` href, so open-in-new-tab,
                // middle-click and copy-link keep working. Only an unmodified
                // primary click is taken over for the lead-prefill navigation;
                // any modified click is left to the browser.
                <Link
                  to={`/quotes/new?customer=${id}`}
                  className="block"
                  onClick={(event) => {
                    if (
                      event.defaultPrevented ||
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    resolveNewQuoteHref(id!).then((href) => navigate(href));
                  }}
                >
                  <Button variant="outline" className="w-full justify-start">
                    Create New Quote
                  </Button>
                </Link>
              )}
              {canAddPolicy && (
                <Link to={`/policies/new?customer=${id}`} className="block">
                  <Button variant="primary" className="w-full justify-start">
                    Add Policy
                  </Button>
                </Link>
              )}
              <Link to={`/policies?customer=${id}`} className="block">
                <Button variant="outline" className="w-full justify-start">
                  View Policies
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>

      {/* A6: record audit footer, below both columns. */}
      <RecordTimestamps createdAt={customer.created_at} updatedAt={customer.updated_at} />

      {/* Delete Confirmation Modal.

          B4 corrected the copy. It said "This action cannot be undone", which
          was not true in either direction: deleting a contact is a SOFT delete
          an administrator can reverse, and a contact holding policies, quotes
          or claims is now refused outright rather than quietly hidden while its
          premium stays on the books. */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteRefusal(null);
        }}
        title="Delete Contact"
        description="The contact is hidden from Contacts, and an administrator can restore it. It cannot be deleted at all while it still has policies, quotes or claims."
      >
        {deleteRefusal && (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20"
            data-testid="delete-refusal"
          >
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              This contact was not deleted.
            </p>
            <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">{deleteRefusal.message}</p>
            {Object.keys(deleteRefusal.blockedBy).length > 0 && (
              <ul className="mt-2 list-inside list-disc text-xs text-amber-800 dark:text-amber-300">
                {Object.entries(deleteRefusal.blockedBy).map(([label, count]) => (
                  <li key={label}>
                    {count} {count === 1 && label.endsWith('s') ? label.slice(0, -1) : label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteRefusal(null);
            }}
          >
            {deleteRefusal ? 'Close' : 'Cancel'}
          </Button>
          {!deleteRefusal && (
            <Button variant="danger" onClick={handleDelete} isLoading={deleteMutation.isPending}>
              Delete Contact
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}

// BROKER-6c: the raw `source` code (or empty) is a broker-facing label. A
// direct/empty source is a contact keyed in by hand. #639: it used to read
// "Customer", which looked like a contact type that contradicted the list's
// "Lead" (the list type is derived from issued policies; source is only
// where the record came from).
const SOURCE_LABELS: Record<string, string> = {
  direct: 'Added directly',
  referral: 'Referral',
  campaign: 'Campaign',
  walk_in: 'Walk-in',
  online: 'Online',
  partner: 'Partner',
  lead: 'Lead Conversion',
  quote: 'Quote Sync',
};

export function sourceLabel(source: string | null): string {
  if (!source) return SOURCE_LABELS.direct;
  return SOURCE_LABELS[source] ?? titleCase(source);
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">{value}</p>
    </div>
  );
}
